import { useEffect, useState, lazy } from 'react'
import { useParams } from 'react-router-dom'

import { Button } from '@/components/button'
import { Menu, MenuItem } from '@/components/interactives/dropdown-menu.tsx'
import BatchEditToolbar from '@/components/rundown/batch-edit-toolbar.tsx'
import KeyboardNavigationHandler from '@/components/rundown/KeyboardNavigationHandler.jsx'
import EndOfShow from '@/components/rundown/timing/EndOfShow.jsx'
import OverUnder from '@/components/rundown/timing/OverUnder.jsx'
import PlannedEnd from '@/components/rundown/timing/PlannedEnd.jsx'
import RundownDayTZ from '@/components/rundown/timing/RundownDayTZ.jsx'
import { UpgradeAlert } from '@/components/UpgradeAlert'
import { openUpgradeDialog } from '@/components/UpgradeDialog.tsx'
import {
  faBolt,
  faChevronDown,
  faHeading,
  faLayerGroup,
  faPlus,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { useAtomValue, useSetAtom } from 'jotai'

import { CUE_BACKGROUND_COLORS } from '~shared/consts'
import { CueType } from '~shared/rundown/cue'
import { RundownPermission } from '~shared/rundown/permission'

import { getIsCuesLimitExceededAtom } from '@/store/plan.store.ts'
import { runnerAtom } from '@/store/runner.store.ts'

import { useAddRundownCue } from '../../entities/rundown/index.ts'
import { useModal } from '../modal-manager/index.ts'
import { useRundownPermission } from '../permissions/lib/use-rundown-permission.ts'
import { RundownPermissionGuard } from '../permissions/ui/rundown-permission-guard.tsx'

import { cueUiActions } from './cue/cue-ui.store.ts'
import { CueList } from './cue/CueList'
import { Grid } from './grid/Grid.tsx'
import { ModalManager } from './modals/ModalManager.tsx'
import { useParentGroupMap, useRundown } from './rundown.derived'
import { RundownHotkeys } from './shortcuts/RundownHotkeys.tsx'

const RundownColoursModal = lazy(
  () => import('@/components/modal/RundownColoursModal.jsx'),
)

export function RundownBody() {
  const rundown = useRundown()
  const parentGroupMap = useParentGroupMap()

  const runner = useAtomValue(runnerAtom)

  const writeAccess = useRundownPermission({
    permission: RundownPermission.EDIT,
  })

  const readonly = !writeAccess

  const { rundownId } = useParams()
  const [cueCountToAdd, setCueCountToAdd] = useState(1)

  const [coloursModal, renderColoursModal] = useModal(RundownColoursModal)

  // Jotai atoms
  const getIsCuesLimitExceed = useSetAtom(getIsCuesLimitExceededAtom)

  const { mutate: addRundownCue, isPending: isAddingRundownCue } =
    useAddRundownCue()

  const isCuesLimitExceeded = getIsCuesLimitExceed(cueCountToAdd)

  const cueTypesTitleMap = {
    [CueType.CUE]: {
      title: 'New cue',
      colour: '',
    },
    [CueType.HEADING]: {
      title: 'New heading',
      backgroundColor: 'transparent',
    },
    [CueType.GROUP]: {
      title: 'New group',
      backgroundColor: CUE_BACKGROUND_COLORS[6],
    },
  }

  async function handleAddCue(type: CueType = CueType.CUE) {
    // Check user limits
    if (isCuesLimitExceeded) {
      return openUpgradeDialog(
        "You've reached your limit for cues on this rundown.",
      )
    }

    addRundownCue(
      {
        rundownId,
        params: {
          count: cueCountToAdd,
        },
        payload: {
          type,
          title: cueTypesTitleMap[type].title,
          backgroundColor: cueTypesTitleMap[type].backgroundColor,
        },
      },
      {
        onSuccess() {
          setCueCountToAdd(1)
        },
      },
    )
  }

  /**
   * Reveal the group holding the cue that is about to run, so the operator can
   * see what is coming without expanding it by hand.
   */
  // useEffect: reacts to the runner advancing, which is state this component
  // does not own and gets no event for.
  useEffect(() => {
    if (!runner?.timesnap.running || !runner.nextCueId) return

    const parentGroupId = parentGroupMap[runner.nextCueId]
    if (!parentGroupId) return

    // `expandGroup`, not a toggle: a group that is already open must stay open.
    cueUiActions.expandGroup(parentGroupId)
  }, [parentGroupMap, runner])

  return (
    <div
      className={[
        'min-w-max w-full flex flex-col gap-0',
        runner?.timesnap?.running ? 'pb-[70vh]' : 'pb-20',
      ].join(' ')}
    >
      <div className="mx-8 my-2 relative">
        <BatchEditToolbar
          rundownId={rundownId}
          setRundownColoursModalOpen={coloursModal.show}
        />
        <RundownDayTZ
          timezone={rundown.timezone}
          readonly={readonly}
        />
      </div>

      <Grid>
        <CueList />
      </Grid>

      {/* Plan limit alert */}
      <div>
        {isCuesLimitExceeded && (
          <div className="m-6">
            <UpgradeAlert
              message="Cues limit exceeded for this rundown. Upgrade to add and edit more cues."
              className="w-fit"
            />
          </div>
        )}
      </div>

      <div className="ml-10 mb-5">
        <div className="inline-flex items-center gap-8 bg-[#161616] rounded-sm px-4 py-3 text-base">
          <PlannedEnd />
          <EndOfShow
            timezone={rundown.timezone}
            todDisplayFormat={rundown.settings.todDisplayFormat}
          />
          <OverUnder />
        </div>
      </div>

      <RundownPermissionGuard permission={RundownPermission.EDIT}>
        <div className="ml-10 flex items-center">
          <div className="flex">
            <Button
              className="text-base font-normal! rounded-r-none tabular-nums"
              text={`Add ${cueCountToAdd > 1 ? cueCountToAdd : ''} cue${cueCountToAdd > 1 ? 's' : ''}`}
              icon={faPlus}
              loading={isAddingRundownCue}
              colour="dark"
              toolTip={
                isCuesLimitExceeded ? (
                  <>
                    Limit reached
                    <FontAwesomeIcon
                      icon={faBolt}
                      className="text-yellow-500"
                    />{' '}
                    Upgrade for more
                  </>
                ) : (
                  ''
                )
              }
              onClick={() => handleAddCue()}
              data-label="create-rundown-button"
            />
            <Menu
              className="rounded-l-none border-l-2 border-gray-800"
              icon={faChevronDown}
            >
              <MenuItem
                icon={faHeading}
                label="Add heading"
                onClick={() => handleAddCue(CueType.HEADING)}
              />
              <MenuItem
                icon={faLayerGroup}
                label="Add group"
                onClick={() => handleAddCue(CueType.GROUP)}
              />
            </Menu>
          </div>
          <label
            htmlFor="addCuesInput"
            className="ml-2 text-sm space-x-1"
          >
            <input
              id="addCuesInput"
              className="px-2 h-9 w-16 bg-transparent focus:outline-hidden focus:ring-3 rounded-sm border border-white/20"
              value={cueCountToAdd}
              onChange={(e) => {
                const min = 1
                const max = 30
                let _val = 1
                if (e.target.value < min) {
                  _val = 1
                } else if (e.target.value > max) {
                  _val = max
                } else {
                  _val = parseInt(e.target.value)
                }
                setCueCountToAdd(_val)
              }}
              type="number"
              step={1}
              min={1}
              max={30}
            />
            <span className="h-7 inline-block text-gray-600">
              more cue
              {cueCountToAdd > 1 && 's'}
            </span>
          </label>
        </div>
      </RundownPermissionGuard>

      <RundownHotkeys />
      <KeyboardNavigationHandler />

      {renderColoursModal({
        colours: rundown.settings,
        rundownId,
      })}

      <ModalManager />
    </div>
  )
}
